import { useNavigate } from 'react-router-dom';
import { AffiliateDisclosure } from '@/components/AffiliateFooter';

export default function TermsOfServicePage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-300" data-testid="terms-of-service-page">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white mb-8 text-sm">&larr; Back</button>
        <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>Terms of Service</h1>
        <p className="text-gray-500 mb-8 text-sm">Last updated: March 16, 2026</p>

        <div className="space-y-6 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-white mb-2">1. Agreement to Terms</h2>
            <p>By accessing or using Brand OS ("the Service"), you agree to be bound by these Terms of Service. Brand OS is operated by a Delaware C Corporation. If you do not agree to these Terms, you may not use the Service.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">2. Description of Service</h2>
            <p>Brand OS is an AI-powered personal improvement platform that provides personalized daily tasks, product recommendations, progress tracking, and content across seven improvement categories. The Service uses artificial intelligence to generate recommendations based on user-provided information.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">3. Account Registration</h2>
            <p>You must provide accurate, complete information when creating an account. You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account. You must be at least 16 years old to use the Service.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">4. Free Trial & Subscription</h2>
            <p>All plans include a 3-day free trial with full access to the Service. You will not be charged during the trial period. After the trial period, you will be automatically charged for the plan you selected unless you cancel before the trial ends.</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li><strong>Monthly Plan:</strong> $12.00 USD per month, billed monthly</li>
              <li><strong>3-Month Plan:</strong> $30.00 USD, billed every 3 months</li>
              <li><strong>Annual Plan:</strong> $84.00 USD, billed annually</li>
            </ul>
            <p className="mt-2">All subscriptions auto-renew at the end of each billing period unless cancelled before the renewal date. You may cancel at any time from your account settings or by contacting support@buildbrandos.com.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">5. Payments & Refunds</h2>
            <p>All payments are processed securely through Stripe. Prices are in USD. You may cancel your subscription at any time; access continues until the end of the current billing period. If you cancel during the free trial, you will not be charged. Refunds are available within 14 days of the first charge if you have not substantially used the Service.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">6. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Upload illegal, harmful, or inappropriate content</li>
              <li>Attempt to reverse-engineer, hack, or compromise the Service</li>
              <li>Use the Service for any unlawful purpose</li>
              <li>Share your account credentials with others</li>
              <li>Resell or redistribute Service content without permission</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">7. Intellectual Property</h2>
            <p>All content, features, and functionality of the Service are owned by Brand OS and protected by copyright and trademark laws. User-generated content (photos, notes) remains your property, but you grant us a limited license to process it for Service functionality.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">8. AI-Generated Content Disclaimer</h2>
            <p>Brand OS uses artificial intelligence to generate personalized recommendations, tasks, and suggestions. This content is for informational purposes only and does not constitute medical, fitness, or professional advice. Always consult qualified professionals before making significant health or lifestyle changes. We are not responsible for outcomes resulting from following AI-generated recommendations.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">9. Product Recommendations & Affiliate Links</h2>
            <p>Brand OS is a participant in the Amazon Services LLC Associates Program, an affiliate advertising program designed to provide a means for sites to earn advertising fees by advertising and linking to Amazon.com.</p>
            <p className="mt-2">The Service may display product recommendations alongside affiliate links. When you click these links and make qualifying purchases on Amazon.com, Brand OS may earn a commission at no additional cost to you. This commission helps support the continued development and maintenance of the Service.</p>
            <p className="mt-2"><strong>Important disclosures per Section 5 of the Amazon Associates Operating Agreement:</strong></p>
            <ul className="list-disc pl-6 space-y-1 mt-1">
              <li>Brand OS does not sell, ship, handle, or fulfill any physical products. All product orders are processed and fulfilled directly by Amazon.com or its third-party sellers.</li>
              <li>Product prices displayed are approximate and may change. Always verify current pricing on Amazon.com before purchasing.</li>
              <li>Product availability, shipping times, and return policies are determined by Amazon.com.</li>
              <li>Brand OS makes no warranties or guarantees regarding the quality, safety, or suitability of any recommended products.</li>
              <li>Affiliate links contain tracking tags (buildbrandos-20). We do not use link shorteners or cloaking in violation of Amazon's policies.</li>
              <li>We do not share affiliate links via email, PDF, or private messaging in compliance with the Associates Program Operating Agreement.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">10. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law, Brand OS shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of your use of the Service, including but not limited to damages related to product recommendations or affiliate purchases. Our total liability shall not exceed the amount you paid for the Service in the 12 months preceding the claim.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">11. Governing Law</h2>
            <p>These Terms are governed by the laws of the State of Delaware, United States, without regard to its conflict of law principles. For users in Canada, nothing in these Terms limits your rights under applicable Canadian consumer protection legislation, including PIPEDA and applicable provincial privacy laws.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">12. Termination</h2>
            <p>We reserve the right to suspend or terminate your account for violation of these Terms. You may delete your account at any time by contacting support@buildbrandos.com.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">13. Changes to Terms</h2>
            <p>We may modify these Terms at any time. Material changes will be communicated via email or in-app notification. Continued use after changes constitutes acceptance.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">14. Contact</h2>
            <p>
              Brand OS<br />
              Email: support@buildbrandos.com<br />
              Registered Agent: LegalInc, 651 N. Broad St. Suite 201, Middletown, DE 19709
            </p>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10">
          <AffiliateDisclosure className="text-gray-500" />
        </div>
      </div>
    </div>
  );
}
